<template>
  <div class="attack-dashboard-charts-wrapper is-fullwidth">
    <div class="charts-row">
      <div class="rbz-card white chart-panel">
        <div class="chart-panel-header rbz-text-large">
          {{ $t('dashboard.charts.cardTitles.traffic') }}
        </div>
        <div class="chart-panel-body big-chart-panel-body">
          <rbz-chart
            v-show="!isTimelineLoading"
            :data="trafficChartData"
            :series-options="trafficChartSeriesOptions"
            :chart-height="200"
            :legend-sum="legendSumConfig"
            :legend-as-tooltip="legendAsTooltipConfig"
            @zoomed="chartsZoomed"
            sync-key="rbzDashboard"
          >
          </rbz-chart>
          <rbz-loader
            :loading="!!isTimelineLoading"
            :data-exists="trafficChartData.length > 0"
            :no-data-message="$t('dashboard.noSearchResultsMessage')"
            height="100%"
          />
        </div>
      </div>
    </div>
    <div class="charts-row">
      <div class="rbz-card white chart-panel response-status-panel">
        <div class="chart-panel-header rbz-text-large">
          {{ $t('dashboard.charts.cardTitles.responseStatus') }}
          <div class="chart-toggle">
            <rbz-form-button
              v-for="toggleItem in toggleList"
              :key="toggleItem.key"
              variant="secondary"
              class="chart-toggle-button"
              data-qa="chart-toggle-button"
              :class="[
                `toggle-${toggleItem.key}`,
                { selected: responseStatusToggle === toggleItem.key },
              ]"
              :label="toggleItem.title"
              @button-click="responseStatusToggle = toggleItem.key"
              :ignore-update-in-process="true"
            >
            </rbz-form-button>
          </div>
        </div>
        <div class="chart-panel-body big-chart-panel-body">
          <rbz-chart
            v-show="!isTimelineLoading"
            :data="responseStatusChartData"
            :series-options="responseStatusChartSeriesOptions"
            :hidden-keys="responseStatusChartHiddenKeys"
            :group-color-palette="responseStatusChartGroupColorPalette"
            :chart-height="200"
            :legend-sum="legendSumConfig"
            :legend-as-tooltip="legendAsTooltipConfig"
            sync-key="rbzDashboard"
          >
          </rbz-chart>
          <rbz-loader
            :loading="!!isTimelineLoading"
            :data-exists="responseStatusChartData.length > 0"
            :no-data-message="$t('dashboard.noSearchResultsMessage')"
            height="100%"
          />
        </div>
      </div>
    </div>
    <div class="charts-row">
      <div class="rbz-card white chart-panel">
        <div class="chart-panel-header rbz-text-large">
          {{ $t('dashboard.charts.cardTitles.sessionIPs') }}
        </div>
        <div class="chart-panel-body">
          <rbz-chart
            v-show="!isTimelineLoading"
            :data="sessionIPsChartData"
            :series-options="sessionIPsChartSeriesOptions"
            :chart-height="200"
            :legend-as-tooltip="{ ...legendAsTooltipConfig }"
            sync-key="rbzDashboard"
          >
          </rbz-chart>
          <rbz-loader
            :loading="!!isTimelineLoading"
            :data-exists="sessionIPsChartData.length > 0"
            :no-data-message="$t('dashboard.noSearchResultsMessage')"
            height="100%"
          />
        </div>
      </div>
      <div class="rbz-card white chart-panel">
        <div class="chart-panel-header rbz-text-large">
          {{ $t('dashboard.charts.cardTitles.totalBandwidth') }}
        </div>
        <div class="chart-panel-body">
          <rbz-chart
            v-show="!isStatsLoading"
            :data="totalBandwidthChartData"
            :series-options="totalBandwidthChartSeriesOptions"
            :chart-height="200"
            :legend-as-tooltip="{ ...legendAsTooltipConfig }"
            sync-key="rbzDashboard"
          >
          </rbz-chart>
          <rbz-loader
            :loading="!!isStatsLoading"
            :data-exists="totalBandwidthChartData.length > 0"
            :no-data-message="$t('dashboard.noSearchResultsMessage')"
            height="100%"
          />
        </div>
      </div>
    </div>
    <div class="charts-row">
      <div class="rbz-card white chart-panel">
        <div class="chart-panel-header rbz-text-large">
          {{ $t('dashboard.charts.cardTitles.requestsCount') }}
        </div>
        <div class="chart-panel-body">
          <rbz-chart
            v-show="!isStatsLoading"
            :data="reblazerSeriesData"
            :series-options="requestsCountChartSeriesOptions"
            :chart-height="200"
            :legend-as-tooltip="{
              ...legendAsTooltipConfig,
              enforceSide: 'right',
              showTopX: {
                startIndex: 2,
                sliceEnd: 20,
              },
            }"
            sync-key="rbzDashboard"
          >
          </rbz-chart>
          <rbz-loader
            :loading="!!isStatsLoading"
            :data-exists="reblazerSeriesData.length > 0"
            :no-data-message="$t('dashboard.noSearchResultsMessage')"
            height="100%"
          />
        </div>
      </div>
      <div class="rbz-card white chart-panel">
        <div class="chart-panel-header rbz-text-large">
          {{ $t('dashboard.charts.cardTitles.bandwidth') }}
        </div>
        <div class="chart-panel-body">
          <rbz-chart
            v-show="!isStatsLoading"
            :data="reblazerSeriesData"
            :series-options="bandwidthChartSeriesOptions"
            :chart-height="200"
            :legend-as-tooltip="{
              ...legendAsTooltipConfig,
              enforceSide: 'middle',
              showTopX: {
                startIndex: 2,
                sliceEnd: 20,
              },
            }"
            sync-key="rbzDashboard"
          >
          </rbz-chart>
          <rbz-loader
            :loading="!!isStatsLoading"
            :data-exists="reblazerSeriesData.length > 0"
            :no-data-message="$t('dashboard.noSearchResultsMessage')"
            height="100%"
          />
        </div>
      </div>
      <div class="rbz-card white chart-panel">
        <div class="chart-panel-header rbz-text-large">
          {{ $t('dashboard.charts.cardTitles.latency') }}
        </div>
        <div class="chart-panel-body">
          <rbz-chart
            v-show="!isStatsLoading"
            :data="reblazerSeriesData"
            :series-options="latencyChartSeriesOptions"
            :chart-height="200"
            :legend-as-tooltip="{
              ...legendAsTooltipConfig,
              enforceSide: 'left',
              showTopX: {
                startIndex: 2,
                sliceEnd: 20,
              },
            }"
            display-fractional-axis-values
            sync-key="rbzDashboard"
          >
          </rbz-chart>
          <rbz-loader
            :loading="isStatsLoading"
            :data-exists="reblazerSeriesData.length > 0"
            :no-data-message="$t('dashboard.noSearchResultsMessage')"
            height="100%"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType } from 'vue';
import RbzChart, {
  GroupColorPalette,
  SeriesOptions,
} from 'ui-component-library/rbz/components/RbzChart.vue';
import { STATUS_COLORS, TRAFFIC_COLORS } from '@/types/const';
import _ from 'lodash';
import { GenericObject } from '@/types';
import RbzLoader from '@/components/RbzLoader.vue';
import RbzFormButton from '@/components/RbzFormButton.vue';
import { RESPONSE_CODE_COLOR_PALETTE } from '@/helpers/Consts';

export default defineComponent({
  name: 'RbzDashboardCharts',
  components: {
    RbzFormButton,
    RbzLoader,
    RbzChart,
  },
  props: {
    timeline: Array as PropType<GenericObject[]>,
    isTimelineLoading: Boolean,
    stats: Array as PropType<GenericObject[]>,
    isStatsLoading: Boolean,
  },
  emits: ['add-filter', 'zoomed'],
  data() {
    return {
      legendSumConfig: {
        active: true,
      },
      legendAsTooltipConfig: {
        active: true,
        width: 'max-content',
        showTopX: {
          startIndex: 1,
          sliceEnd: 20,
        },
      },
      toggleList: [
        {
          key: 'all',
          title: this.$t('dashboard.charts.toggle.all'),
        },
        {
          key: 'origin',
          title: this.$t('dashboard.charts.toggle.origin'),
        },
        {
          key: 'waap',
          title: this.$t('dashboard.charts.toggle.waap'),
        },
      ],
      responseStatusToggle: 'all',
      responseStatusSelectedCodes: [],
      hostnamesColors: {} as GenericObject,
    };
  },
  watch: {
    responseStatusExistingCodes: {
      handler: function (val) {
        this.responseStatusSelectedCodes = _.cloneDeep(val);
      },
      immediate: true,
      deep: true,
    },
  },
  computed: {
    trafficChartData(): GenericObject[] {
      const returnArray = [];
      for (const dataItem of this.timeline) {
        const hits = dataItem['num_of_requests'];
        const blocked = dataItem['num_of_blocked_requests'];
        const originBlocked = dataItem['num_of_origin_blocked_requests'];
        const challenge = dataItem['num_of_challenges'];
        const passed = hits - blocked - originBlocked - challenge;
        const humans = dataItem['num_of_human_requests'];
        const bots = hits - humans;
        returnArray.push({
          timeframe: dataItem['time_period'] * 1000,
          hits: hits > 0 ? hits : 0,
          blocked: blocked > 0 ? blocked : 0,
          originBlocked: originBlocked > 0 ? originBlocked : 0,
          challenge: challenge > 0 ? challenge : 0,
          passed: passed > 0 ? passed : 0,
          humans: humans > 0 ? humans : 0,
          bots: bots > 0 ? bots : 0,
        });
      }
      return returnArray;
    },

    trafficChartSeriesOptions(): SeriesOptions[] {
      const hits: SeriesOptions = {
        key: 'Hits',
        title: this.$t('dashboard.charts.series.hits'),
        fieldName: 'hits',
        show: true,
        drawStyle: 'spline',
        strokeColor: TRAFFIC_COLORS['hits'],
      };
      const blocked: SeriesOptions = {
        key: 'Blocked',
        title: this.$t('dashboard.charts.series.blocked'),
        fieldName: 'blocked',
        show: true,
        drawStyle: 'spline',
        strokeColor: TRAFFIC_COLORS['blocked'],
      };
      const originBlocked: SeriesOptions = {
        key: 'Origin Blocked',
        title: this.$t('dashboard.charts.series.originBlocked'),
        fieldName: 'originBlocked',
        show: true,
        drawStyle: 'spline',
        strokeColor: TRAFFIC_COLORS['originBlocked'],
      };
      const challenge: SeriesOptions = {
        key: 'Challenges',
        title: this.$t('dashboard.charts.series.challenges'),
        fieldName: 'challenge',
        show: true,
        drawStyle: 'spline',
        strokeColor: TRAFFIC_COLORS['challenge'],
      };
      const passed: SeriesOptions = {
        key: 'Passed',
        title: this.$t('dashboard.charts.series.passed'),
        fieldName: 'passed',
        show: true,
        drawStyle: 'spline',
        strokeColor: TRAFFIC_COLORS['passed'],
      };
      const humans: SeriesOptions = {
        key: 'Humans',
        title: this.$t('dashboard.charts.series.humans'),
        fieldName: 'humans',
        show: true,
        drawStyle: 'spline',
        strokeColor: TRAFFIC_COLORS['humans'],
      };
      const bots: SeriesOptions = {
        key: 'Bots',
        title: this.$t('dashboard.charts.series.bots'),
        fieldName: 'bots',
        show: true,
        drawStyle: 'spline',
        strokeColor: TRAFFIC_COLORS['bots'],
      };
      const seriesOptions: SeriesOptions[] = [
        hits,
        blocked,
        originBlocked,
        challenge,
        passed,
        humans,
        bots,
      ];
      return _.filter(seriesOptions, (singleSeriesOptions: SeriesOptions) => {
        return _.some(this.trafficChartData, (dataItem) => {
          return dataItem[singleSeriesOptions.fieldName] > 0;
        });
      });
    },

    responseStatusExistingCodes(): string[] {
      const codes = _.flatMapDeep(this.timeline, (dataItem) => {
        let waapCodes = _.filter(dataItem['array_status_codes'], (statusCodeItem) => {
          return !_.isNil(statusCodeItem.status);
        });
        waapCodes = _.map(waapCodes, (statusCodeItem) => {
          return `status${statusCodeItem.status}`;
        });
        let originCodes = _.filter(dataItem['array_origin_status_codes'], (statusCodeItem) => {
          return !_.isNil(statusCodeItem.status);
        });
        originCodes = _.map(originCodes, (statusCodeItem) => {
          return `originStatus${statusCodeItem.status}`;
        });
        return _.concat([waapCodes, originCodes]);
      });
      return _.uniq(codes).sort();
    },

    responseStatusChartData(): GenericObject[] {
      const returnArray = [];
      for (const dataItem of this.timeline) {
        const statusesObject: GenericObject = {
          timeframe: dataItem['time_period'] * 1000,
        };
        for (const statusKey of this.responseStatusExistingCodes) {
          let value;
          if (statusKey.startsWith('status')) {
            value = _.find(dataItem['array_status_codes'], (statusCodeItem) => {
              return statusCodeItem.status?.toString() === statusKey.slice(-3);
            })?.['num_of_requests'];
          }
          if (statusKey.startsWith('origin')) {
            value = _.find(dataItem['array_origin_status_codes'], (statusCodeItem) => {
              return statusCodeItem.status?.toString() === statusKey.slice(-3);
            })?.['num_of_requests'];
          }
          statusesObject[statusKey] = value > 0 ? value : 0;
        }
        returnArray.push(statusesObject);
      }
      return returnArray;
    },

    responseStatusChartSeriesOptions(): SeriesOptions[] {
      let seriesOptions: SeriesOptions[] = _.map(
        this.responseStatusExistingCodes,
        (key: string) => {
          const title = key.replace('status', '').replace('originStatus', '');
          const subtitle = key.startsWith('origin')
            ? this.$t('dashboard.charts.series.origin')
            : '';
          return {
            key: `${title} ${subtitle}`.trim(),
            title: title,
            subtitle: subtitle,
            fieldName: key,
            show: true,
            drawStyle: 'spline',
            strokeColor: this.getStatusColor(key),
            group: `${title.slice(0, -2)}xx`,
            forceDropdownOnOne: true,
          } as SeriesOptions;
        }
      );
      seriesOptions = _.sortBy(seriesOptions, (singleSeriesOptions: SeriesOptions) => {
        return singleSeriesOptions.title;
      });
      seriesOptions = _.filter(seriesOptions, (singleSeriesOptions: SeriesOptions) => {
        return (
          _.some(this.responseStatusChartData, (dataItem) => {
            return dataItem[singleSeriesOptions.fieldName] > 0;
          }) && this.responseStatusSelectedCodes.includes(singleSeriesOptions.fieldName)
        );
      });
      return seriesOptions;
    },

    responseStatusChartHiddenKeys(): string[] {
      const keys = this.responseStatusExistingCodes.filter((key: string) => {
        if (this.responseStatusToggle === 'all') {
          return false;
        }
        if (this.responseStatusToggle === 'origin') {
          return !key.startsWith('origin');
        }
        if (this.responseStatusToggle === 'waap') {
          return key.startsWith('origin');
        }
      });
      return _.map(keys, (key: string) => {
        const title = key.replace('status', '').replace('originStatus', '');
        const subtitle = key.startsWith('origin') ? this.$t('dashboard.charts.series.origin') : '';
        return `${title} ${subtitle}`.trim();
      });
    },

    responseStatusChartGroupColorPalette(): GroupColorPalette {
      return RESPONSE_CODE_COLOR_PALETTE;
    },

    sessionIPsChartData(): GenericObject[] {
      const returnArray = [];
      for (const dataItem of this.timeline) {
        const sessions = dataItem['num_of_sessions'];
        const ips = dataItem['num_of_ip'];
        returnArray.push({
          timeframe: dataItem['time_period'] * 1000,
          sessions: sessions > 0 ? sessions : 0,
          ips: ips > 0 ? ips : 0,
        });
      }
      return returnArray;
    },

    sessionIPsChartSeriesOptions(): SeriesOptions[] {
      return [
        {
          key: 'Sessions',
          title: this.$t('dashboard.charts.series.sessions'),
          fieldName: 'sessions',
          show: true,
          drawStyle: 'spline',
          strokeColor: TRAFFIC_COLORS['sessions'],
        },
        {
          key: 'IPs',
          title: this.$t('dashboard.charts.series.ips'),
          fieldName: 'ips',
          show: true,
          drawStyle: 'spline',
          strokeColor: TRAFFIC_COLORS['ips'],
        },
      ];
    },

    totalBandwidthChartData(): GenericObject[] {
      const returnArray = [];
      for (const dataItem of this.stats) {
        const bandwidth = dataItem['sum_of_bandwidth'];
        returnArray.push({
          timeframe: dataItem['time_period'] * 1000,
          bandwidth: bandwidth > 0 ? bandwidth : 0,
        });
      }
      return returnArray;
    },

    totalBandwidthChartSeriesOptions(): SeriesOptions[] {
      return [
        {
          key: 'Bits',
          title: this.$t('dashboard.charts.series.bits'),
          fieldName: 'bandwidth',
          show: true,
          drawStyle: 'spline',
          strokeColor: TRAFFIC_COLORS['bandwidth'],
        },
      ];
    },

    requestsCountChartSeriesOptions(): SeriesOptions[] {
      const returnArray: SeriesOptions[] = [];
      _.forEach(this.groupedStatsByHostname, (group: GenericObject[], groupName: string) => {
        if (!this.hostnamesColors[groupName]) {
          this.hostnamesColors[groupName] = this.generateColor();
        }
        const groupColor = this.hostnamesColors[groupName];
        returnArray.push({
          key: groupName,
          title: groupName,
          fieldName: `${groupName}-counter`,
          show: true,
          drawStyle: 'spline',
          strokeColor: groupColor,
        });
      });
      returnArray.unshift({
        key: 'Total amount',
        title: this.$t('dashboard.charts.series.reblazersTotalAmount'),
        fieldName: `hostsAmount`,
        show: false,
        enforceShowInLegendTooltip: true,
        strokeColor: `rgba(0, 0, 0, 0)`,
      });
      return returnArray;
    },

    bandwidthChartSeriesOptions(): SeriesOptions[] {
      const returnArray: SeriesOptions[] = [];
      _.forEach(this.groupedStatsByHostname, (group: GenericObject[], groupName: string) => {
        if (!this.hostnamesColors[groupName]) {
          this.hostnamesColors[groupName] = this.generateColor();
        }
        const groupColor = this.hostnamesColors[groupName];
        returnArray.push({
          key: groupName,
          title: groupName,
          fieldName: `${groupName}-bandwidth`,
          show: true,
          drawStyle: 'spline',
          strokeColor: groupColor,
        });
      });
      returnArray.unshift({
        key: 'Total amount',
        title: this.$t('dashboard.charts.series.reblazersTotalAmount'),
        fieldName: `hostsAmount`,
        show: false,
        enforceShowInLegendTooltip: true,
        strokeColor: `rgba(0, 0, 0, 0)`,
      });
      return returnArray;
    },

    latencyChartSeriesOptions(): SeriesOptions[] {
      const returnArray: SeriesOptions[] = [];
      _.forEach(this.groupedStatsByHostname, (group: GenericObject[], groupName: string) => {
        if (!this.hostnamesColors[groupName]) {
          this.hostnamesColors[groupName] = this.generateColor();
        }
        const groupColor = this.hostnamesColors[groupName];
        returnArray.push({
          key: groupName,
          title: groupName,
          fieldName: `${groupName}-latency`,
          show: true,
          drawStyle: 'spline',
          strokeColor: groupColor,
        });
      });
      returnArray.unshift({
        key: 'Total amount',
        title: this.$t('dashboard.charts.series.reblazersTotalAmount'),
        fieldName: `hostsAmount`,
        show: false,
        enforceShowInLegendTooltip: true,
        strokeColor: `rgba(0, 0, 0, 0)`,
      });
      return returnArray;
    },

    reblazerSeriesData(): GenericObject[] {
      const returnArray: GenericObject[] = [];
      _.forEach(this.stats, (dataItem: GenericObject) => {
        const seriesDataObject: GenericObject = {
          timeframe: dataItem['time_period'] * 1000,
        };
        seriesDataObject[`${dataItem.hostname}-counter`] = dataItem['num_of_requests'];
        seriesDataObject[`${dataItem.hostname}-bandwidth`] = dataItem['sum_of_bandwidth'];
        seriesDataObject[`${dataItem.hostname}-latency`] = dataItem['avg_latency'].toFixed(2);
        returnArray.push(seriesDataObject);
      });
      _.forEach(_.groupBy(this.stats, 'time_period'), (group, timePeriod) => {
        returnArray.push({
          timeframe: Number(timePeriod) * 1000,
          hostsAmount: group.length,
        });
      });
      return returnArray;
    },

    groupedStatsByHostname(): GenericObject {
      return _.groupBy(this.stats, 'hostname');
    },
  },
  methods: {
    getStatusColor(status: string) {
      if (!status) {
        return `#000`;
      }
      return STATUS_COLORS[status] || `#000`;
    },

    generateColor(): string {
      return '#' + ('00000' + ((Math.random() * (1 << 24)) | 0).toString(16)).slice(-6);
    },

    chartsZoomed(newBounds: { min: number; max: number }) {
      this.$emit('zoomed', newBounds);
    },
  },
});
</script>

<style scoped lang="scss">
@use 'ui-component-library/assets/styles/colors' as colors;
@use 'sass:map';

.attack-dashboard-charts-wrapper {
  .rbz-card {
    margin-bottom: 0;
  }
}

.charts-row {
  align-content: space-between;
  display: flex;
  gap: 30px;
  margin-bottom: 20px;
}

.chart-panel {
  min-width: 0;
  width: 100%;
}

:deep(.chart-panel-header) {
  color: map.get(colors.$colors, 'bright-gray');
  display: flex;
  height: 56px;
  justify-content: space-between;
  padding: 16px 24px;

  .chart-toggle-button {
    display: inline-flex;
  }

  .chart-toggle-button .rbz-button {
    align-items: center;
    border-radius: 0;
    color: map.get(colors.$colors, 'stratos');
    display: inline-flex;
    height: 24px;
    justify-content: center;
    min-width: 43px;
    padding: 3px 7px;
  }

  .chart-toggle-button .rbz-button:focus,
  .chart-toggle-button .rbz-button:hover,
  .chart-toggle-button .rbz-button:active {
    border: 1px solid map.get(colors.$colors, 'jewel');
    color: map.get(colors.$colors, 'white');
  }

  .chart-toggle-button:first-child .rbz-button {
    border-radius: 4px 0 0 4px;
  }

  .chart-toggle-button:last-child .rbz-button {
    border-radius: 0 4px 4px 0;
  }

  .chart-toggle-button.selected .rbz-button {
    background-color: map.get(colors.$colors, 'white-ice');
    border: 1px solid map.get(colors.$colors, 'jewel');
    color: map.get(colors.$colors, 'jewel');
  }
}

.chart-panel-body {
  min-height: 220px;
  padding: 16px 24px;
  position: relative;
}

.big-chart-panel-body {
  min-height: 300px;
}
</style>
